import {
  Accordion,
  Flex,
  Grid,
  Link,
  LoadingIcon,
  Message,
  Stack,
  Text,
} from "basis";
import { getHref } from "library/api";
import { useQuery } from "react-query";
import { SvgInline } from "../../SvgInline/SvgInline";
import { Statement } from "./Statement";
import { StatementsResponse, YearStatements } from "./types";

type Props = {
  href: string;
};

export const Statements = ({ href }: Props) => {
  const statementsInfo = useQuery(["statements", { href }], async () => {
    const statements = await getHref<StatementsResponse>(href);
    const result: YearStatements[] = [];

    statements.data.data
      .sort((s1, s2) => (s1.statement_date > s2.statement_date ? -1 : 1))
      .forEach((statement) => {
        const year = statement.statement_date.slice(0, 4);

        if (result[result.length - 1]?.[0] === year) {
          result[result.length - 1][1].push(statement);
        } else {
          result.push([year, [statement]]);
        }
      });

    return result;
  });
  const statements = statementsInfo.data;

  if (statementsInfo.isLoading) {
    return (
      <Flex height="200" placeItems="center">
        <LoadingIcon size="medium" />
      </Flex>
    );
  }

  if (statementsInfo.isError) {
    return (
      <Stack gap="6">
        <SvgInline name="error-documents" size="92" />
        <Text textStyle="heading5" textStyle-sm="heading4">
          Oops, something went wrong
        </Text>
        <Text>
          We’ve encountered an unexpected problem and are experiencing
          difficulties in retrieving your statements.
        </Text>
        <Text>
          {`Please try again later or `}
          <Link href="/contact" newTab={false}>
            contact Latitude
          </Link>
          {` if the problem persists.`}
        </Text>
      </Stack>
    );
  }

  if (!statements || statements.length === 0) {
    return (
      <Stack gap="6">
        <SvgInline name="icon-view-statements" size="92" />
        <Text textStyle="heading5" textStyle-sm="heading4">
          You don’t have any new statements
        </Text>
        <Text>
          Please check back later to view, download or print a copy of your
          statement.
          <br />
          {`Visit the `}
          <Link href="/faq" newTab={false}>
            frequently asked questions
          </Link>
          {` to find information on how you can access your full statement history.`}
        </Text>
      </Stack>
    );
  }

  return (
    <Grid rowsGap="6">
      <Accordion>
        {statements.map(([year, yearStatements]) => (
          <Accordion.Item
            initiallyOpen={
              statements.length === 1 ||
              year === String(new Date().getFullYear())
            }
            key={year}
          >
            <Accordion.Item.Header>{year} Statements</Accordion.Item.Header>
            <Accordion.Item.Content padding="0">
              {yearStatements.map((statement, index) => (
                <Statement
                  statement={statement}
                  isFirst={index === 0}
                  key={statement.statement_date}
                />
              ))}
            </Accordion.Item.Content>
          </Accordion.Item>
        ))}
      </Accordion>
      <Message
        severity="assistance"
        bg="secondary.lightBlue.t25"
        title="Where are my previous statements?"
      >
        Please visit the{" "}
        <Link href="/faq" newTab={false}>
          frequently asked questions
        </Link>{" "}
        to find information on how you can access your full statement history.
      </Message>
    </Grid>
  );
};
